import { useState } from "react";
import { API_URL } from "../lib/api";

const COPY_FEEDBACK_DURATION_MS = 2000;

interface UseReportActionsResult {
  isGeneratingPdf: boolean;
  isCopied: boolean;
  generatePdf: (report: string, companyName: string) => Promise<void>;
  copyToClipboard: (report: string) => Promise<void>;
}

export function useReportActions(onError: (message: string) => void): UseReportActionsResult {
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const generatePdf = async (report: string, companyName: string) => {
    if (!report || isGeneratingPdf) return;

    setIsGeneratingPdf(true);
    try {
      const response = await fetch(`${API_URL}/generate-pdf`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          report_content: report,
          company_name: companyName || report,
        }),
      });

      if (!response.ok) {
        throw new Error("Falha ao gerar o PDF");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${companyName || "research_report"}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
      onError(error instanceof Error ? error.message : "Falha ao gerar o PDF");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const copyToClipboard = async (report: string) => {
    if (!report) return;

    try {
      await navigator.clipboard.writeText(report);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), COPY_FEEDBACK_DURATION_MS);
    } catch (err) {
      console.error("Failed to copy text: ", err);
      onError("Falha ao copiar para a área de transferência");
    }
  };

  return { isGeneratingPdf, isCopied, generatePdf, copyToClipboard };
}
